
import {Component, Vue, Watch} from 'vue-property-decorator';
import dayjs from 'dayjs';
import {FlowMeter} from '@/data/models/meter';
import {Provider, ProviderLabel} from '@/data/models/enums/provider-enum';

type FlowState = 'normal' | 'late' | 'critical' | 'none';
type GroupBy = 'building' | 'type' | 'provider';

interface FlowItem {
  meter: FlowMeter;
  state: FlowState;
  buildingLabel: string;
}

interface FlowGroup {
  key: string;
  name: string;
  items: FlowItem[];
  // État le plus frais du groupe : anime le lien vers la base
  state: FlowState;
  lateCount: number;
  criticalCount: number;
}

const STATES: { key: FlowState, label: string, caption: string, color: string }[] = [
  {key: 'normal', label: 'Normal', caption: 'dernière donnée il y a moins de 3 jours', color: 'success'},
  {key: 'late', label: 'En retard', caption: 'aucune donnée depuis 3 à 7 jours', color: 'orange'},
  {key: 'critical', label: 'Critique', caption: 'aucune donnée depuis plus d\'une semaine', color: 'error'},
  {key: 'none', label: 'Aucune donnée', caption: 'jamais reçu', color: 'grey'},
];
const STATE_ORDER: FlowState[] = STATES.map((it) => it.key);

const GROUP_BY: { key: GroupBy, label: string, icon: string }[] = [
  {key: 'building', label: 'Bâtiment', icon: 'apartment'},
  {key: 'type', label: 'Type de données', icon: 'category'},
  {key: 'provider', label: 'Provider', icon: 'input'},
];

// Rechargement complet (filet de sécurité si des événements sont perdus) et recalcul des états avec le temps
const REFRESH_DELAY = 5 * 60 * 1000;
const TICK_DELAY = 30 * 1000;
const RECONNECT_DELAY = 5 * 1000;
const PULSE_DURATION = 2500;
const ARRIVALS_COUNT = 6;
// Arrivées gardées en mémoire : la recherche n'en affiche qu'une partie
const ARRIVALS_BUFFER = 200;

/**
 * Onglet « Flux » : compteurs regroupés par bâtiment, type de données ou provider, chaque groupe relié à la base
 * de données. Replié, un groupe montre un carré par compteur ; déplié, une ligne par compteur.
 * Couleur selon l'ancienneté de la dernière donnée : normal < 3 j, en retard 3 à 7 j, critique > 7 j.
 * Temps réel : les relevés insérés arrivent par SSE (/admin/logs/flow/stream) et font clignoter le compteur concerné.
 * Regroupement, recherche et filtres d'état conservés dans l'URL (?groupBy, provider par défaut, ?q, ?states) : partageables
 * et retrouvés au retour depuis un compteur. Les compteurs supprimés et calculés (formule) sont exclus côté API,
 * ceux sans bâtiment ici.
 */
@Component({})
export default class AdminDataFlow extends Vue {
  states = STATES;
  groupByOptions = GROUP_BY;
  meters: FlowMeter[] = [];
  loading = true;
  error = false;
  refreshedAt: Date = null;
  search = typeof this.$route.query.q === 'string' ? this.$route.query.q : '';
  stateFilter: FlowState[] = typeof this.$route.query.states === 'string'
      ? this.$route.query.states.split(',').filter((it): it is FlowState => STATE_ORDER.includes(it as FlowState))
      : [];
  groupBy: GroupBy = GROUP_BY.some((it) => it.key === this.$route.query.groupBy) ? this.$route.query.groupBy as GroupBy : 'provider';
  expanded: Record<string, boolean> = {};
  timer: number = null;
  tickTimer: number = null;
  now = Date.now();

  // Temps réel
  stream: EventSource = null;
  live = false;
  reconnectTimer: number = null;
  destroyed = false;
  pulses: Record<string, boolean> = {};
  arrivals: { id: number, meter: FlowMeter, date: Date }[] = [];
  arrivalId = 0;

  @Watch('groupBy')
  onGroupByChange(value: GroupBy): void {
    this.expanded = {};
    this.updateQuery({groupBy: value});
  }

  @Watch('search')
  onSearchChange(value: string): void {
    // Nouvelle recherche : les groupes reprennent leur dépliage automatique
    this.expanded = {};
    this.updateQuery({q: value ? value : undefined});
  }

  @Watch('stateFilter')
  onStateFilterChange(value: FlowState[]): void {
    this.updateQuery({states: value.length > 0 ? value.join(',') : undefined});
  }

  /** replace : le retour depuis un compteur ramène à l'écran filtré, sans repasser par chaque frappe */
  updateQuery(values: Record<string, string | undefined>): void {
    const query = {...this.$route.query, ...values};
    Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);
    this.$router.replace({query}).catch(() => undefined);
  }

  mounted(): void {
    this.load();
    this.connect();
    this.timer = window.setInterval(() => this.load(), REFRESH_DELAY);
    this.tickTimer = window.setInterval(() => this.now = Date.now(), TICK_DELAY);
  }

  beforeDestroy(): void {
    this.destroyed = true;
    window.clearInterval(this.timer);
    window.clearInterval(this.tickTimer);
    window.clearTimeout(this.reconnectTimer);
    if (this.stream) {
      this.stream.close();
    }
  }

  /** Connexion au flux SSE ; en cas de coupure, reconnexion avec un jeton à jour puis rechargement (événements manqués) */
  connect(): void {
    this.$dao.logs.openDataFlowStream((readings) => this.onReadings(readings)).then((stream) => {
      if (this.destroyed) {
        stream.close();
        return;
      }
      this.stream = stream;
      stream.onopen = () => this.live = true;
      stream.onerror = () => {
        stream.close();
        this.live = false;
        this.reconnectTimer = window.setTimeout(() => {
          this.connect();
          this.load();
        }, RECONNECT_DELAY);
      };
    }).catch(() => {
      this.reconnectTimer = window.setTimeout(() => this.connect(), RECONNECT_DELAY);
    });
  }

  onReadings(readings: { meterId: string, date: Date }[]): void {
    readings.forEach((reading) => {
      const meter = this.meters.find((it) => it.meterId === reading.meterId);
      // Compteurs absents du flux (calculés, liés à une référence…) : ignorés
      if (!meter) {
        return;
      }
      if (!meter.lastInsert || reading.date > meter.lastInsert) {
        meter.lastInsert = reading.date;
      }
      this.arrivals = [{id: ++this.arrivalId, meter, date: new Date()}, ...this.arrivals].slice(0, ARRIVALS_BUFFER);
      this.$set(this.pulses, meter.meterId, true);
      window.setTimeout(() => this.$set(this.pulses, meter.meterId, false), PULSE_DURATION);
    });
  }

  load(): void {
    this.loading = true;
    this.$dao.logs.getDataFlow().then((meters) => {
      this.meters = meters;
      this.error = false;
      this.refreshedAt = new Date();
    }).catch(() => this.error = true)
        .finally(() => this.loading = false);
  }

  stateOf(meter: FlowMeter): FlowState {
    if (!meter.lastInsert) {
      return 'none';
    }
    const days = dayjs(this.now).diff(meter.lastInsert, 'day', true);
    return days < 3 ? 'normal' : days <= 7 ? 'late' : 'critical';
  }

  typeLabel(type: string): string {
    const key = `meter.types.${type}`;
    const label = this.$t(key).toString();
    return label === key ? (type || 'Type inconnu') : label;
  }

  providerLabel(provider: Provider): string {
    return ProviderLabel[provider] || ProviderLabel[Provider.NONE];
  }

  groupOf(item: FlowItem): { key: string, name: string } {
    switch (this.groupBy) {
      case 'type':
        return {key: item.meter.meterType, name: this.typeLabel(item.meter.meterType)};
      case 'provider':
        return {key: item.meter.provider, name: this.providerLabel(item.meter.provider)};
      default:
        return {key: item.meter.buildingId, name: item.buildingLabel};
    }
  }

  /** Précision affichée sous le nom d'un compteur déplié : ce que le regroupement ne dit pas déjà */
  itemDetail(item: FlowItem): string {
    const provider = this.providerLabel(item.meter.provider);
    return this.groupBy === 'building' ? `${this.typeLabel(item.meter.meterType)} · ${provider}`
        : this.groupBy === 'type' ? `${item.buildingLabel} · ${provider}`
            : `${item.buildingLabel} · ${this.typeLabel(item.meter.meterType)}`;
  }

  // Compteurs sans bâtiment (bâtiment supprimé) masqués : ils ne sont plus suivis
  get items(): FlowItem[] {
    return this.meters.filter((meter) => meter.buildingName).map((meter) => ({
      meter,
      state: this.stateOf(meter),
      buildingLabel: [meter.clientName, meter.buildingName].filter((n) => n).join(' - '),
    }));
  }

  get counts(): Record<FlowState, number> {
    const counts = {normal: 0, late: 0, critical: 0, none: 0};
    this.items.forEach((it) => counts[it.state]++);
    return counts;
  }

  get searchText(): string {
    return this.search ? this.search.toLowerCase().trim() : '';
  }

  /** Recherche sur le nom du compteur, son bâtiment, son type et son provider */
  matchesSearch(meter: FlowMeter): boolean {
    const label = [meter.clientName, meter.buildingName].filter((n) => n).join(' - ');
    return !this.searchText || [meter.meterName, label, this.typeLabel(meter.meterType), this.providerLabel(meter.provider)]
        .some((text) => text.toLowerCase().includes(this.searchText));
  }

  get visibleArrivals(): { id: number, meter: FlowMeter, date: Date }[] {
    return this.arrivals.filter((it) => this.matchesSearch(it.meter)).slice(0, ARRIVALS_COUNT);
  }

  /** Un groupe ouvert ou fermé à la main garde ce choix ; sinon il est déplié dès qu'une recherche est saisie */
  isExpanded(group: FlowGroup): boolean {
    return this.expanded[group.key] !== undefined ? this.expanded[group.key] : !!this.searchText;
  }

  get groups(): FlowGroup[] {
    const byKey: Record<string, FlowGroup> = {};

    this.items
        .filter((it) => this.stateFilter.length === 0 || this.stateFilter.includes(it.state))
        .filter((it) => this.matchesSearch(it.meter))
        .forEach((it) => {
          const {key, name} = this.groupOf(it);
          if (!byKey[key]) {
            byKey[key] = {key, name, items: [], state: 'none', lateCount: 0, criticalCount: 0};
          }
          byKey[key].items.push(it);
        });

    const groups = Object.values(byKey);
    groups.forEach((g) => {
      g.items.sort((a, c) => a.meter.meterName.localeCompare(c.meter.meterName));
      g.state = STATE_ORDER.find((s) => g.items.some((it) => it.state === s));
      g.lateCount = g.items.filter((it) => it.state === 'late').length;
      g.criticalCount = g.items.filter((it) => it.state === 'critical').length;
    });
    return groups.sort((a, b) => a.name.localeCompare(b.name));
  }

  isGroupPulsing(group: FlowGroup): boolean {
    return group.items.some((it) => this.pulses[it.meter.meterId]);
  }

  arrivalTime(date: Date): string {
    return dayjs(date).format('HH:mm:ss');
  }

  get allExpanded(): boolean {
    return this.groups.length > 0 && this.groups.every((g) => this.isExpanded(g));
  }

  toggleGroup(group: FlowGroup): void {
    this.$set(this.expanded, group.key, !this.isExpanded(group));
  }

  toggleAll(): void {
    const value = !this.allExpanded;
    const expanded: Record<string, boolean> = {};
    this.groups.forEach((g) => expanded[g.key] = value);
    this.expanded = expanded;
  }

  toggleState(state: FlowState): void {
    this.stateFilter = this.stateFilter.includes(state) ? this.stateFilter.filter((it) => it !== state) : [...this.stateFilter, state];
  }

  age(item: FlowItem): string {
    return item.meter.lastInsert ? item.meter.fromNow : 'aucune donnée';
  }

  itemTitle(item: FlowItem): string {
    const date = item.meter.lastInsert ? `${dayjs(item.meter.lastInsert).format('YYYY-MM-DD HH:mm')} (${item.meter.fromNow})` : 'aucune donnée';
    return `${item.meter.meterName} — ${date}`;
  }

  showMeter(meter: FlowMeter): void {
    this.$router.push({name: 'detailMeter', params: {buildingId: meter.buildingId, meterId: meter.meterId}});
  }
}
